import React from 'react';
import { kpiDisplayValue } from './kpiFormat';
import { rangeLabel } from '../insightsRange';

// The PURE renderer for a Summary row: it draws a stored strip and nothing else.
//
// Live tiles are drawn by SummaryRow, which owns a query per tile. This one exists
// for the two paths that must not run queries — the PDF export, and the first paint
// before the live results arrive — and for the same reason every other type has an
// entry in RENDERERS: registering here is what makes the picker offer the type.
//
// Same prop contract as the other renderers ({ data, width, height, title }) so the
// card and the wizard preview can treat every type alike. `data` is the whole
// payload (see renderers/index.js::rendererInput), shaped
// `{ shape: 'summary-row', tiles: [{ label, result, error_type }] }`. No message: the
// backend's text is not carried into storage, so there is none to read here.

// A tile that failed says so in its own cell. The strip keeps its shape, because a
// missing cell would silently renumber the row and change what the others mean.
// What a STORED tile says when it has no number. Every phrase here is written BY US and
// chosen by the failure kind. The backend's own message is never shown: some of
// those are the agent's prose, but others name internal field names, Python types
// and, on the agent-error path, an upstream service's raw error verbatim. Nothing in
// the payload marks which is which, so the frontend cannot tell a sentence meant for
// a reader from one meant for us — and a tile has room for two words, not a triage.
//
// Two buckets, plus the specific cases. "Couldn't answer" means the question did not
// produce a usable number and the tile needs editing. "Unavailable" means something
// outside the question went wrong and it is worth trying again.
const FAILURE_TEXT = {
  permission_denied: 'No access',
  empty_result: 'No data',
  not_single_value: 'Not one value',
  truncated: 'Answer cut short',
  // A family whose flag is off is a closed door, not a failure. Saying "unavailable"
  // would read as something broken and send someone looking for a fault there is not.
  feature_disabled: 'Not enabled',
  // The question ran but its answer cannot be a tile's number.
  validation_failed: "Couldn't answer",
  type_mismatch: "Couldn't answer",
  agent_error: "Couldn't answer",
  // Something outside the question.
  unknown_metric: 'Unavailable',
  network_error: 'Unavailable',
};

export function tileDisplay(tile) {
  if (!tile || typeof tile !== 'object') return { value: '—', failed: true, note: '' };
  if (tile.error_type) {
    // The failure kind alone decides the phrase. A stored tile carries no backend
    // message to fall back to, by design -- it is not written in the first place -- and
    // an older row that still has one is ignored rather than rendered.
    return { value: FAILURE_TEXT[tile.error_type] || 'Unavailable', failed: true, note: '' };
  }
  const result = tile.result;
  // A platform tile carries a bare number; a prompted tile carries a single-value
  // payload. kpiDisplayValue is total over both, so neither needs a type check here.
  const value = typeof result === 'number' ? kpiDisplayValue({ value_kind: 'number', value: result }) : kpiDisplayValue(result);
  return { value, failed: false, note: String(result?.value_label || '').trim() };
}

// The strip wraps rather than squeezes. Below roughly this much width per tile the
// numbers stop being readable at a glance, which is the only thing the family is
// for, so two rows of three beats one row of six slivers.
const MIN_TILE_PX = 150;
export function tilesPerRow(width, count) {
  const w = Number(width) || 0;
  if (!w || !count) return count || 1;
  return Math.max(1, Math.min(count, Math.floor(w / MIN_TILE_PX)));
}

function SummaryRowRenderer({ data, width, title }) {
  const tiles = Array.isArray(data?.tiles) ? data.tiles : [];
  if (!tiles.length) {
    return <div className="wc-sr-empty">This summary row has no tiles yet.</div>;
  }
  const perRow = tilesPerRow(width, tiles.length);
  return (
    <div
      className="wc-sr-strip"
      role="group"
      aria-label={title ? `${title}, ${tiles.length} tiles` : `${tiles.length} tiles`}
      style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
    >
      {tiles.map((tile, i) => {
        const { value, failed, note } = tileDisplay(tile);
        return (
          <div className={'wc-sr-tile' + (failed ? ' wc-sr-tile-failed' : '')} key={i}>
            <span className="wc-sr-tile-label">{tile?.label || `Tile ${i + 1}`}</span>
            <span className="wc-sr-tile-value">{value}</span>
            {note && !failed && <span className="wc-sr-tile-note">{note}</span>}
            {/* PRD-1467: each tile's own range, persisted in the stored tile (prompted
                tiles only; platform counts store time_range: null). */}
            {tile?.time_range && <span className="wc-sr-tile-range">{rangeLabel(tile.time_range, tile.resolved_time_range || tile.result?.resolved_time_range)}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default SummaryRowRenderer;
